import { useCallback, useEffect, useState } from 'react'
import { amplitudeService } from '@/services/amplitude.service'
import type { AmplitudeUserProperties, AmplitudeState } from '@/types/amplitude'

/**
 * React hook for Amplitude Analytics
 * Provides a convenient interface for tracking events and managing user properties
 */
export const useAmplitude = () => {
  const [state, setState] = useState<AmplitudeState>(amplitudeService.getState())

  // Update state when Amplitude state changes
  useEffect(() => {
    // Check state periodically (mainly for initialization status)
    const checkState = () => {
      const currentState = amplitudeService.getState()
      setState(currentState)
    }

    // Check immediately
    checkState()

    // Check again after a short delay (in case initialization is in progress)
    const timeoutId = setTimeout(checkState, 1000)

    return () => clearTimeout(timeoutId)
  }, [])

  /**
   * Identify a user
   */
  const identify = useCallback((userId: string, userProperties?: AmplitudeUserProperties) => {
    amplitudeService.identify(userId, userProperties)
  }, [])

  /**
   * Update user properties
   */
  const setUserProperties = useCallback((userProperties: AmplitudeUserProperties) => {
    amplitudeService.setUserProperties(userProperties)
  }, [])

  /**
   * Reset the current user
   */
  const reset = useCallback(() => {
    amplitudeService.reset()
  }, [])

  /**
   * Track an event (placeholder for future implementation)
   */
  const track = useCallback((eventName: string, eventProperties?: Record<string, any>) => {
    amplitudeService.track(eventName, eventProperties)
  }, [])

  return {
    // State
    isInitialized: state.isInitialized,
    isEnabled: state.isEnabled,

    // Methods
    identify,
    setUserProperties,
    reset,
    track,
  }
}

export default useAmplitude
